import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import AccountInvitesPanel from "@/components/account/AccountInvitesPanel";
import OrgMembersPanel from "@/components/account/OrgMembersPanel";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/pricing/LockedFeatureCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ToastProvider";
import { createBillingCheckout, createBillingPortal, fetchBillingSummary } from "@/api/billing";
import { useSessionUser } from "@/hooks/useSessionUser";
import { useUpgradeModal } from "@/hooks/useUpgradeModal";

export default function OrgSettingsPage() {
  const { user, organization } = useSessionUser();
  const { openUpgradeModal } = useUpgradeModal();
  const { push } = useToast();
  const [searchParams] = useSearchParams();
  const planConfig = useMemo(
    () => ({
      STANDARD: { label: "Standard", base: 24, included: 1, seat: 14, max: 4 },
      PRO: { label: "Pro", base: 44, included: 1, seat: 19, max: 10 },
      AGENCY: { label: "Agency", base: 99, included: 5, seat: 19, max: 20 },
    }),
    [],
  );
  const [planTier, setPlanTier] = useState<keyof typeof planConfig>("STANDARD");
  const [seatCount, setSeatCount] = useState(1);
  const [billingStatus, setBillingStatus] = useState<string | null>(null);
  const [seatLimit, setSeatLimit] = useState<number | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const promoCode = useMemo(
    () => searchParams.get("promo") || searchParams.get("promo_code") || null,
    [searchParams],
  );

  useEffect(() => {
    const loadSummary = async () => {
      setSummaryLoading(true);
      try {
        const summary = await fetchBillingSummary();
        if (summary?.seat_count) {
          setSeatCount(summary.seat_count);
        }
        if (typeof summary?.seat_limit === "number") {
          setSeatLimit(summary.seat_limit);
        }
        const normalizedTier =
          summary?.tier && summary.tier.toUpperCase() in planConfig
            ? (summary.tier.toUpperCase() as keyof typeof planConfig)
            : "STANDARD";
        setPlanTier(normalizedTier);
        setBillingStatus(summary?.subscription_status ?? null);
      } catch (err) {
        push({ title: "Billing", message: err instanceof Error ? err.message : "Unable to load billing." });
      } finally {
        setSummaryLoading(false);
      }
    };
    void loadSummary();
  }, [push]);

  useEffect(() => {
    const config = planConfig[planTier];
    let next = seatCount;
    if (next < config.included) next = config.included;
    if (config.max && next > config.max) next = config.max;
    if (next !== seatCount) setSeatCount(next);
  }, [planConfig, planTier, seatCount]);

  return (
    <div className="aa-page-shell space-y-8">
      <header className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-primary/80">Team</p>
        <h1 className="aa-page-title">Team Settings</h1>
        <p className="text-sm text-muted-foreground">Invite collaborators and manage organization access.</p>
      </header>

      <section className="grid gap-4 md:grid-cols-2">
        <Card className="aa-glass-card">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Organization</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-foreground/80">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Name</p>
              <p className="text-base font-semibold text-foreground">{organization?.name ?? "Unknown"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">ID</p>
              <p className="font-mono text-sm text-foreground/70">{organization?.id ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="aa-glass-card">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Current user</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-foreground/80">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Name</p>
              <p className="text-base font-semibold text-foreground">{user?.name ?? "Unknown"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Email</p>
              <p className="text-foreground/70">{user?.email ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Role</p>
              <p className="text-foreground/70 capitalize">{user?.role ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
      </section>

      <Card className="aa-glass-card">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Billing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-foreground/80">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Subscription status</p>
              <p className="text-base font-semibold text-foreground">
                {summaryLoading ? "Loading…" : billingStatus ? billingStatus : "Not subscribed"}
              </p>
            </div>
            {promoCode && (
              <div className="rounded-full border border-border/60 px-3 py-1 text-xs uppercase tracking-wide text-muted-foreground">
                Promo: {promoCode}
              </div>
            )}
          </div>
          <div className="grid gap-2 md:grid-cols-[220px,1fr] md:items-center">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Seats</p>
              <p className="text-xs text-muted-foreground">
                {planConfig[planTier].label} includes {planConfig[planTier].included} seat
                {planConfig[planTier].included > 1 ? "s" : ""}. Extra seats are ${planConfig[planTier].seat}/user.
              </p>
            </div>
            <Input
              type="number"
              min={planConfig[planTier].included}
              max={planConfig[planTier].max ?? undefined}
              value={seatCount}
              onChange={(event) => setSeatCount(Number(event.target.value) || 1)}
              className="w-full"
            />
          </div>
          <div className="grid gap-2 md:grid-cols-[220px,1fr] md:items-center">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Plan</p>
              <p className="text-xs text-muted-foreground">
                ${planConfig[planTier].base}/mo base. {planConfig[planTier].max ? `Up to ${planConfig[planTier].max} seats.` : "No seat cap."}
              </p>
            </div>
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
              value={planTier}
              onChange={(event) => setPlanTier(event.target.value as keyof typeof planConfig)}
            >
              {Object.entries(planConfig).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>
          {typeof seatLimit === "number" && (
            <p className="text-xs text-muted-foreground">
              Current seat limit: {seatLimit}. Active users: {seatCount}.
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={async () => {
                setBillingLoading(true);
                try {
                  const response = await createBillingCheckout({
                    tier: planTier,
                    seats_total: seatCount,
                    promo_code: promoCode ?? undefined,
                  });
                  if (response?.checkout_url) {
                    window.location.href = response.checkout_url;
                  }
                } catch (err) {
                  push({ title: "Billing", message: err instanceof Error ? err.message : "Unable to start checkout." });
                } finally {
                  setBillingLoading(false);
                }
              }}
              disabled={billingLoading}
            >
              {billingLoading ? "Starting checkout…" : "Start subscription"}
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                setPortalLoading(true);
                try {
                  const response = await createBillingPortal();
                  if (response?.portal_url) {
                    window.location.href = response.portal_url;
                  }
                } catch (err) {
                  push({ title: "Billing", message: err instanceof Error ? err.message : "Unable to open portal." });
                } finally {
                  setPortalLoading(false);
                }
              }}
              disabled={portalLoading}
            >
              {portalLoading ? "Opening portal…" : "Manage billing"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Promo codes apply to the first seat only and are auto-applied from the URL when present.
          </p>
        </CardContent>
      </Card>

      <CapabilityGate
        flag="plan.plus"
        variant="card"
        fallback={
          <LockedFeatureCard
            title="Team access"
            description="Invite teammates to your organization with shared contacts and leads (Pro/Agency only)."
            onUpgrade={openUpgradeModal}
            flag="plan.plus"
          />
        }
      >
        <AccountInvitesPanel organizationId={organization?.id ?? null} organizationName={organization?.name ?? null} />
      </CapabilityGate>

      {["owner", "admin"].includes((user?.role ?? "").toLowerCase()) && <OrgMembersPanel />}
    </div>
  );
}
